import React, { useContext, useState } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { FiCopy } from "react-icons/fi";
import { createApp } from "../../../services/integration.service";
import { ToastrContext } from "../../../provider/toastrContext";

const CreateAppModal = ({ show, onClose, onCreated }) => {
  const { showSuccess, showError, showCopy } = useContext(ToastrContext);
  const [stage, setStage] = useState("form");
  const [submitting, setSubmitting] = useState(false);
  const [createdApp, setCreatedApp] = useState(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: { environment: "live" },
  });

  const reallyClose = () => {
    setStage("form");
    setCreatedApp(null);
    reset({ environment: "live" });
    onClose();
  };

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      const body = {
        name: data.name,
        environment: data.environment,
      };
      if (data.description) body.description = data.description;
      if (data.homepage_url) body.homepage_url = data.homepage_url;
      const res = await createApp(body);
      setCreatedApp(res);
      setStage("secret");
      onCreated();
      showSuccess("App created successfully");
    } catch (e) {
      showError((e && e.message) || "Failed to create app");
    } finally {
      setSubmitting(false);
    }
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      showCopy(`${label} copied`);
    } catch {
      showError("Copy failed");
    }
  };

  const bootstrap = createdApp && createdApp.bootstrap_credential;
  const keyId = bootstrap ? bootstrap.key_id || "" : "";
  const secret = bootstrap ? bootstrap.secret || "" : "";

  return (
    <Modal
      show={show}
      onHide={reallyClose}
      centered
      backdrop={stage === "secret" ? "static" : true}
    >
      {stage === "form" ? (
        <>
          <Modal.Header closeButton>
            <Modal.Title>Create App</Modal.Title>
          </Modal.Header>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Modal.Body>
              <div className="mb-3">
                <label className="col-form-label">
                  Name<span className="required">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${
                    errors.name ? "is-invalid" : ""
                  }`}
                  {...register("name", {
                    required: "Name is required",
                    maxLength: {
                      value: 255,
                      message: "Name must be 255 characters or fewer",
                    },
                  })}
                />
                {errors.name && (
                  <div className="invalid-feedback">{errors.name.message}</div>
                )}
              </div>
              <div className="mb-3">
                <label className="col-form-label">Description</label>
                <textarea
                  className="form-control"
                  rows={2}
                  {...register("description")}
                />
              </div>
              <div className="mb-3">
                <label className="col-form-label">Homepage URL</label>
                <input
                  type="url"
                  placeholder="https://"
                  className={`form-control ${
                    errors.homepage_url ? "is-invalid" : ""
                  }`}
                  {...register("homepage_url", {
                    pattern: {
                      value: /^https?:\/\//i,
                      message: "Must start with http:// or https://",
                    },
                  })}
                />
                {errors.homepage_url && (
                  <div className="invalid-feedback">
                    {errors.homepage_url.message}
                  </div>
                )}
              </div>
              <div className="mb-3">
                <label className="col-form-label">Environment</label>
                <Form.Select {...register("environment", { required: true })}>
                  <option value="live">Live</option>
                  <option value="test">Test</option>
                </Form.Select>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={reallyClose}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={submitting}>
                {submitting ? "Creating..." : "Create"}
              </Button>
            </Modal.Footer>
          </form>
        </>
      ) : (
        <>
          <Modal.Header>
            <Modal.Title>Save your bootstrap secret</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="alert alert-warning">
              <strong>This is the only time you'll see this secret.</strong>{" "}
              Store it somewhere safe — you can't retrieve it later.
            </div>
            <div className="mb-3">
              <label className="col-form-label">Key ID</label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  value={keyId}
                  readOnly
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => copy(keyId, "Key ID")}
                  disabled={!keyId}
                >
                  <FiCopy />
                </Button>
              </div>
            </div>
            <div className="mb-3">
              <label className="col-form-label">Secret</label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control font-monospace"
                  value={secret}
                  readOnly
                />
                <Button
                  variant="outline-secondary"
                  onClick={() => copy(secret, "Secret")}
                  disabled={!secret}
                >
                  <FiCopy />
                </Button>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={reallyClose}>
              I've saved it
            </Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
};

export default CreateAppModal;
